import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import { categories, hotAnime, nbaVideos, featuredAnime } from '@/mocks/homeData';
import type { AnimeItem, NbaVideo } from '@/mocks/homeData';
import { useState, useMemo } from 'react';

const allItems: (AnimeItem | NbaVideo)[] = [...featuredAnime, ...hotAnime];

const categoryMeta: Record<string, { title: string; desc: string }> = {
  reboot: { title: '热血动漫', desc: '燃爆全场的战斗与冒险，最热血的动漫都在这里' },
  love: { title: '恋爱动漫', desc: '甜蜜温馨的恋爱故事，感受心动的每一个瞬间' },
  funny: { title: '搞笑动漫', desc: '轻松愉快的搞笑日常，让你开怀大笑' },
  'sci-fi': { title: '科幻动漫', desc: '未来世界的无限想象，探索未知的奇妙旅程' },
  horror: { title: '悬疑动漫', desc: '扣人心弦的悬疑推理，每一集都让你欲罢不能' },
  sports: { title: '运动动漫', desc: '热血澎湃的体育竞技，感受青春的激情' },
  music: { title: '音乐动漫', desc: '旋律交织的音乐世界，用心聆听每一首曲目' },
  nba: { title: 'NBA赛事', desc: '精彩NBA赛事视频同步更新，永久免费在线观看' },
};

// Map anime categories to slugs
const animeToCategorySlug = (anime: AnimeItem): string => {
  const c = anime.category;
  if (c === '热血' || c === '冒险') return 'reboot';
  if (c === '恋爱') return 'love';
  if (c === '搞笑' || c === '日常') return 'funny';
  if (c === '科幻') return 'sci-fi';
  if (c === '悬疑') return 'horror';
  if (c === '运动') return 'sports';
  if (c === '音乐') return 'music';
  return 'reboot';
};

const filteredBySlug = (slug: string): (AnimeItem | NbaVideo)[] => {
  if (slug === 'nba') return nbaVideos;
  return allItems.filter((item) => {
    if ('teams' in item) return false;
    return animeToCategorySlug(item as AnimeItem) === slug;
  });
};

type FilterKey = 'all' | 'ongoing' | 'finished' | 'rating' | 'views';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const meta = slug ? categoryMeta[slug] : null;
  const items = slug ? filteredBySlug(slug) : [];
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');

  const sortedItems = useMemo(() => {
    const list = [...items];
    if (activeFilter === 'ongoing') {
      return list.filter((i) => 'status' in i && (i as AnimeItem).status === '连载中');
    }
    if (activeFilter === 'finished') {
      return list.filter((i) => 'status' in i && (i as AnimeItem).status === '已完结');
    }
    if (activeFilter === 'rating') {
      return list
        .filter((i) => 'rating' in i)
        .sort((a, b) => (b as AnimeItem).rating - (a as AnimeItem).rating);
    }
    if (activeFilter === 'views') {
      return list.sort((a, b) => {
        const extractNum = (v: string) => parseFloat(v.replace(/[亿万千]/g, (m) => (m === '亿' ? '00000000' : m === '万' ? '0000' : ''))) || 0;
        const aViews = 'views' in a ? extractNum(a.views) : 0;
        const bViews = 'views' in b ? extractNum(b.views) : 0;
        return bViews - aViews;
      });
    }
    return list;
  }, [items, activeFilter]);

  if (!meta && slug) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = meta ? `${meta.title} - 51动漫 | 免费在线观看${meta.title}` : '分类 - 51动漫';
  const pageDesc = meta ? `${meta.desc}。51动漫提供最新${meta.title}资源，永久免费在线观看。` : '';

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageTitle,
    "description": pageDesc,
    "url": `https://www.51dm.com/category/${slug}`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.51dm.com/" },
        { "@type": "ListItem", "position": 2, "name": meta?.title || slug }
      ]
    }
  };

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDesc}
        keywords={`51动漫, ${meta?.title || ''}, 动漫在线观看, 免费动漫, ${meta?.title || ''}推荐`}
        canonicalUrl={`https://www.51dm.com/category/${slug}`}
        schemaData={[collectionSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          {/* Breadcrumb */}
          <div className="pt-20 pb-0 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
              <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-400">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 font-medium">{meta?.title || slug}</span>
              </nav>
            </div>
          </div>

          {/* Page header */}
          <div className="bg-background-100 border-y border-background-200/50 py-8 md:py-10">
            <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {meta?.title || slug}
              </h1>
              <p className="text-foreground-500 text-sm">{meta?.desc}</p>
            </div>
          </div>

          {/* Filter bar */}
          <div className="sticky top-[60px] z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200/50">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {([
                  { key: 'all', label: '全部' },
                  { key: 'ongoing', label: '连载中' },
                  { key: 'finished', label: '已完结' },
                  { key: 'rating', label: '评分最高' },
                  { key: 'views', label: '播放最多' },
                ] as { key: FilterKey; label: string }[]).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setActiveFilter(f.key)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeFilter === f.key
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content grid */}
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
            {sortedItems.length === 0 ? (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {sortedItems.map((item, idx) => {
                  const isNba = 'teams' in item;
                  const id = item.id;
                  const title = item.title;
                  const cover = item.cover;
                  const views = item.views;

                  return (
                    <Link
                      key={id}
                      to={`/article/${id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                        <img
                          src={cover}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
                          loading={idx < 8 ? 'eager' : 'lazy'}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                          <span className="text-white text-xs font-medium">点击查看详情</span>
                        </div>
                        {isNba && (
                          <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-accent-500/90 text-white text-xs font-medium">
                            NBA
                          </div>
                        )}
                        {!isNba && (
                          <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-accent-500 text-xs font-bold">
                            {(item as AnimeItem).rating}
                          </div>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
                        {title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{isNba ? (item as NbaVideo).date : (item as AnimeItem).episode}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line text-[10px]"></i>
                          {views}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}